import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Plus, Trash2, ClipboardList, FileDown, Loader2, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import api, { projectApi } from '../lib/api';

export default function SimpleTaskList({ projectId, tasks = [], onUpdate }) {
  const [newTask, setNewTask] = useState('');
  const [adding, setAdding] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [estimates, setEstimates] = useState([]);
  const [loadingEstimates, setLoadingEstimates] = useState(false);
  const [importingId, setImportingId] = useState(null);

  const completedCount = tasks.filter(t => t.completed).length;
  const totalCount = tasks.length;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;
    setAdding(true);
    try {
      await api.post(`/projects/${projectId}/tasks`, { name: newTask.trim() });
      setNewTask('');
      onUpdate();
    } catch {
      toast.error('Failed to add task');
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (taskId, completed) => {
    try {
      await api.put(`/projects/${projectId}/tasks/${taskId}`, { completed: !completed });
      onUpdate();
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await api.delete(`/projects/${projectId}/tasks/${taskId}`);
      onUpdate();
    } catch {
      toast.error('Failed to delete task');
    }
  };

  const openImport = async () => {
    setImportOpen(true);
    setLoadingEstimates(true);
    try {
      const res = await projectApi.getEstimatesForTasks(projectId);
      setEstimates(res.data || []);
    } catch {
      toast.error('Failed to load estimates');
    } finally {
      setLoadingEstimates(false);
    }
  };

  const handleImport = async (estimateId) => {
    setImportingId(estimateId);
    try {
      const res = await projectApi.importTasksFromEstimate(projectId, estimateId);
      const { imported, skipped } = res.data;
      if (imported === 0) {
        toast.info(skipped > 0 ? 'All those tasks already exist' : 'No line items to import');
      } else {
        toast.success(`Imported ${imported} task${imported === 1 ? '' : 's'}${skipped ? ` (${skipped} skipped)` : ''}`);
      }
      setImportOpen(false);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import tasks');
    } finally {
      setImportingId(null);
    }
  };

  return (
    <Card className="rounded-sm" data-testid="simple-task-list">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Tasks
            {totalCount > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                {completedCount}/{totalCount} complete
              </span>
            )}
          </span>
          <div className="flex items-center gap-3">
            {totalCount > 0 && (
              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full transition-all"
                  style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }}
                />
              </div>
            )}
            <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={openImport}
              data-testid="import-tasks-btn">
              <FileDown className="h-4 w-4 mr-1.5" /> Import from Estimate
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {/* Add task form */}
        <form onSubmit={handleAdd} className="flex gap-2">
          <Input
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            placeholder="Add a task..."
            className="h-9 rounded-sm text-sm"
            data-testid="new-task-input"
          />
          <Button type="submit" size="sm" disabled={adding || !newTask.trim()}
            className="rounded-sm bg-orange-500 hover:bg-orange-600 h-9 px-3"
            data-testid="add-task-btn">
            <Plus className="h-4 w-4" />
          </Button>
        </form>

        {/* Task list */}
        {tasks.length === 0 ? (
          <p className="text-center text-muted-foreground py-4 text-sm">
            No tasks yet. Add one above or import from an estimate.
          </p>
        ) : (
          <div className="divide-y">
            {tasks.map(task => (
              <div key={task.id} className="flex items-center gap-3 py-2.5 group" data-testid={`task-${task.id}`}>
                <Checkbox
                  checked={task.completed}
                  onCheckedChange={() => handleToggle(task.id, task.completed)}
                  data-testid={`task-check-${task.id}`}
                />
                <span className={`flex-1 text-sm ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                  {task.name}
                </span>
                <Button size="sm" variant="ghost"
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                  onClick={() => handleDelete(task.id)}
                  data-testid={`task-delete-${task.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Import from Estimate dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md" data-testid="import-tasks-dialog">
          <DialogHeader>
            <DialogTitle>Import Tasks from Estimate</DialogTitle>
            <DialogDescription>
              Each estimate line item becomes a task. Duplicates (by name) are skipped.
            </DialogDescription>
          </DialogHeader>
          {loadingEstimates ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : estimates.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm">
              No estimates found for this project. Build or import one in the Estimates tab first.
            </p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {estimates.map((est) => (
                <div key={est.id}
                  className="flex items-center justify-between gap-2 border rounded-sm p-3"
                  data-testid={`import-estimate-${est.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate flex items-center gap-1.5">
                      <Calculator className="h-3.5 w-3.5 text-orange-500 shrink-0" />
                      {est.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {est.estimate_number ? `${est.estimate_number} · ` : ''}
                      {est.line_item_count} line item{est.line_item_count === 1 ? '' : 's'}
                    </p>
                  </div>
                  <Button size="sm" className="rounded-sm bg-orange-500 hover:bg-orange-600 shrink-0"
                    disabled={importingId === est.id || est.line_item_count === 0}
                    onClick={() => handleImport(est.id)}
                    data-testid={`import-estimate-btn-${est.id}`}>
                    {importingId === est.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : 'Import'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
